import {
  compact,
  find,
  get,
  isEmpty,
  map,
  size,
  some,
  filter,
  forEach,
} from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useMemo } from 'react'
import { useImmer } from 'use-immer'
import { Flex } from '@changingai/react-editor-common-component'

import { spotlightsSchema, spotlightSchema } from './schema'
import { purifySchemaDoc } from '@schema'
import { inputFactory, useRenderConfig, generateSubDocProps } from '@widget'

function SpotlightWrapper({ context, onDirty, onUpdateContext, onError }) {
  const [editContext, setEditContext] = useImmer(context)

  const renderer = useRenderConfig({
    onError,
    onUpdateContext: setEditContext,
    onDirty,
    editContext,
    currentSchema: spotlightSchema,
    currentPath: ['spotlightSchema'],
  })

  const configs = useMemo(
    () => [
      inputFactory(spotlightSchema, 'title'),
      inputFactory(spotlightSchema, 'brief'),
      inputFactory(spotlightSchema, 'detail', {
        inputAttributes: {
          multilines: true,
          rows: 5,
        },
      }),
    ],
    [],
  )
  useEffect(() => {
    onUpdateContext(draft => {
      const changedFields = filter(
        Object.keys(spotlightSchema),
        field => draft[field] !== editContext[field],
      )
      if (!isEmpty(changedFields))
        forEach(changedFields, field => {
          draft[field] = editContext[field]
        })
    })
  }, [editContext, onUpdateContext])

  return (
    <Flex width="100%" flexWrap="wrap">
      {configs.map(config => renderer(config))}
    </Flex>
  )
}

SpotlightWrapper.propTypes = {
  context: PropTypes.object.isRequired,
  onDirty: PropTypes.func.isRequired,
  onUpdateContext: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
}

export function Spotlights({
  context,
  onDirty,
  onUpdateContext,
  onError,
  currentPath,
}) {
  const [editContext, setEditContext] = useImmer({
    spotlight_summary: get(
      find(context.attributes, { key: 'spotlight_summary' }),
      'value',
      '',
    ),
    spotlight: JSON.parse(
      get(find(context.attributes, { key: 'spotlight' }), 'value', '[]'),
    ),
  })

  useEffect(() => {
    const { spotlight_summary, spotlight } = purifySchemaDoc(
      editContext,
      spotlightsSchema,
    )

    const lackSpotlightSummaryOrSpotlight =
      size(compact(map([spotlight_summary, spotlight], isEmpty))) === 1
    onError(
      'spotlight',
      lackSpotlightSummaryOrSpotlight,
      `${map(spotlightsSchema, 'label').join('/')}必需同時設定}`,
    )

    const attributes = some([spotlight_summary, spotlight], isEmpty)
      ? []
      : [
          { key: 'spotlight_summary', value: spotlight_summary },
          { key: 'spotlight', value: JSON.stringify(spotlight) },
        ]
    onUpdateContext(draft => {
      draft['attributes'] = attributes
    })
  }, [editContext, onUpdateContext, onError])

  const renderer = useRenderConfig({
    onError,
    onUpdateContext: setEditContext,
    onDirty,
    editContext,
    currentSchema: spotlightsSchema,
    currentPath,
  })

  const configs = useMemo(
    () => [
      inputFactory(spotlightsSchema, 'spotlight_summary'),
      {
        ...generateSubDocProps(spotlightsSchema, 'spotlight'),
        itemConfigs: [
          {
            widget: 'CustomizedInput',
            RenderComp: SpotlightWrapper,
          },
        ],
      },
    ],
    [],
  )

  return (
    <Flex width="100%" flexWrap="wrap">
      {configs.map(config => renderer(config))}
    </Flex>
  )
}

Spotlights.propTypes = {
  context: PropTypes.object.isRequired,
  onDirty: PropTypes.func.isRequired,
  onUpdateContext: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  currentPath: PropTypes.array.isRequired,
}
